import { Options } from '../index';
import Api from '../library/Api';
import Cookie, { CookieKey } from '../library/Cookie';
import EventEmitter from '../library/EventEmitter';
import Tracker from '../library/Tracker';
import { trackerEventNames } from '../library/Constants';
import { utils } from '../utils/Utils';
import { ICampaignData } from 'interface/ICampaignData';

class CampaignService extends EventEmitter {
  public static readonly EXPIRATION_TIME = 1 / 24; // -> 1/24th of a day = 1hour
  public static readonly ENDPOINT_CAMPAIGN_SERVICE = utils.getAbsoluteResourcePath('campaignService');
  public static readonly CACHE_KEY = 'campaign-data';
  public static readonly PARAMS_CACHE_KEY = 'campaign-request-params';

  private static readonly DEFAULT_CAMPAIGN_DATA: ICampaignData = {
    act: 'ACT0000000100ACT',
    partner: '',
    channel: '',
    usage: '',
    offer: '',
    brand: ''
  };

  private api: Api;
  private tracker: Tracker;
  private requestFailed = false;

  public options: Options;

  constructor(api: Api, tracker: Tracker, options: Options) {
    super();
    this.api = api;
    this.tracker = tracker;
    this.options = options;
  }

  private getParams() {
    const { act, locale } = this.options;

    return {
      act,
      locale,
      hasReferrer: !!utils.getReferrer(),
      referrer: utils.getReferrerDomain(),
      landingUrl: this.extractLandingUrl()
    };
  }

  private extractLandingUrl() {
    return utils
      .getLandingUrl()
      .split('?')
      .shift()
      .replace(/http[s]?:\/\//i, '');
  }

  /**
   * True when the last request failed and getData() returned the default campaign data. A successful
   * response can carry the default ACT too, so the ACT alone doesn't tell the two apart.
   */
  public hasFailed(): boolean {
    return this.requestFailed;
  }

  public async getData(): Promise<ICampaignData> {
    const params = this.getParams();
    const url = CampaignService.ENDPOINT_CAMPAIGN_SERVICE + utils.generateGetParameterString(params);

    const payload = {
      cacheKey: CampaignService.CACHE_KEY,
      paramsCacheKey: CampaignService.PARAMS_CACHE_KEY,
      params: this.getParams()
    };

    this.tracker.track(trackerEventNames.CAMPAIGN_SERVICE_REQUEST_STARTED, {
      act: params.act,
      requestPayload: {
        act: params.act,
        locale: params.locale,
        hasReferrer: params.hasReferrer,
        referrer: params.referrer,
        landingUrl: params.landingUrl
      },
      requestUrl: url
    });

    try {
      const data = await this.api.get(url, payload);

      if (data.hasOwnProperty('act')) {
        Cookie.set(CookieKey.ACT, JSON.stringify(data), CampaignService.EXPIRATION_TIME);
      }

      Cookie.set(CookieKey.ACT, params.act);
      this.requestFailed = false;

      return data;
    } catch (error) {
      this.requestFailed = true;
      this.tracker.track(trackerEventNames.CAMPAIGN_SERVICE_REQUEST_FAILED, {
        act: params.act,
        error,
        url,
        payload
      });

      this.tracker.track(trackerEventNames.CAMPAIGN_SERVICE_USING_DEFAULT_ACT, {
        act: params.act,
        message: 'Campaign service failed - using default ACT value',
        defaultAct: CampaignService.DEFAULT_CAMPAIGN_DATA.act,
        originalError: error,
        url,
        payload
      });

      return { ...CampaignService.DEFAULT_CAMPAIGN_DATA };
    }
  }
}

export default CampaignService;
